/**
 * Owned Media — helpers de UI sin JSX: etiquetas traducibles del schema (con el
 * español del doc como fallback), tono de los badges por estado, formato de
 * valores y lectura de los errores estructurados que manda el backend.
 */
import type { TFunction } from 'i18next';
import { TRPCClientError } from '@trpc/client';
import { formatPercent, formatDateMonth, type OmlIssue } from '../../../../shared/oml-rules';
import { OML_LISTS, OML_SCHEMA, type OmlChildType, type OmlEntityType, type OmlFichaStatus, type OmlFieldDef, type OmlListId } from '../../../../shared/types/oml';
import type { BadgeTone } from '@/components/ui/enum-badge';

export const fieldLabel = (t: TFunction, type: OmlEntityType, key: string): string => {
  const def = OML_SCHEMA[type].fields.find((f) => f.key === key);
  return t(`oml.fields.${type}.${key}`, def?.label ?? key);
};
export const fieldHelp = (t: TFunction, type: OmlEntityType, def: OmlFieldDef): string | undefined =>
  def.help ? t(`oml.help.${type}.${def.key}`, def.help) : undefined;
export const entityLabel = (t: TFunction, type: OmlEntityType, plural = false): string =>
  t(`oml.entities.${type}.${plural ? 'plural' : 'one'}`, plural ? OML_SCHEMA[type].labelPlural : OML_SCHEMA[type].label);
export const listLabel = (t: TFunction, id: OmlListId): string => t(`oml.lists.${id}`, OML_LISTS[id].label);

/** Valores de las listas cerradas se muestran tal cual (idioma del contenido = español, §7.3). */
export const TAB_ORDER: { key: string; types: OmlChildType[] }[] = [
  { key: 'summary', types: [] },
  { key: 'brands', types: ['brand'] },
  { key: 'stakeholders', types: ['stakeholder', 'commChannel'] },
  { key: 'platforms', types: ['platform'] },
  { key: 'journeys', types: ['journey'] },
  { key: 'campaigns', types: ['campaign'] },
  { key: 'experiments', types: ['experiment'] },
  { key: 'projects', types: ['project'] },
  { key: 'wow', types: ['process'] },
  { key: 'rules', types: ['channelRule', 'segment', 'tag'] },
  { key: 'reports', types: ['report', 'link'] },
  { key: 'team', types: ['assignment'] },
];

const STATUS_TONES: Record<string, BadgeTone> = {
  // cuenta
  'En onboarding': 'info', Activa: 'success', Pausada: 'warning', Cerrada: 'muted', Archivada: 'muted',
  // journey
  Propuesto: 'neutral', 'En diseño': 'info', 'En implementación': 'accent', Activo: 'success', Pausado: 'warning', Descartado: 'muted', Archivado: 'muted',
  // medición / experimento
  Medido: 'success', 'Sin medición': 'neutral', 'Medición rota': 'danger', Diseñado: 'info', Corriendo: 'accent',
  // proyecto
  'Bajada recibida': 'neutral', 'Esperando assets': 'warning', 'En producción': 'accent', 'En revisión interna': 'info', 'En validación del cliente': 'info', Aprobado: 'success', Entregado: 'success', Cancelado: 'muted',
  // acceso
  'Cuenta compartida del cliente': 'danger', 'Cuenta personal': 'danger', 'SSO Winclap': 'success', 'Cuenta nominal Winclap': 'success', 'Cuenta nominal del cliente': 'info',
};
export const statusTone = (value: unknown): BadgeTone => STATUS_TONES[String(value)] ?? 'neutral';

export const FICHA_TONE: Record<OmlFichaStatus, BadgeTone> = { al_dia: 'success', desactualizada: 'danger', no_aplica: 'muted' };
export const fichaLabel = (t: TFunction, s: OmlFichaStatus): string =>
  s === 'al_dia' ? t('oml.ficha.al_dia', 'Al día') : s === 'desactualizada' ? t('oml.ficha.desactualizada', 'Desactualizada') : t('oml.ficha.no_aplica', 'No aplica');

export function formatFieldValue(t: TFunction, def: OmlFieldDef, v: unknown, ctx: { names?: Record<string, string>; relationNames?: Record<string, string> } = {}): string {
  if (v === null || v === undefined || v === '' || (Array.isArray(v) && !v.length)) return '—';
  switch (def.type) {
    case 'bool': return v ? t('common.yes', 'Sí') : t('common.no', 'No');
    case 'percent': return typeof v === 'number' ? formatPercent(v, { sign: def.key === 'uplift' }) : String(v);
    case 'number': return typeof v === 'number' ? v.toLocaleString() : String(v);
    case 'dateMonth': return formatDateMonth(v);
    case 'user': return ctx.names?.[String(v).toLowerCase()] ?? String(v);
    case 'multiselect': return Array.isArray(v) ? v.join(' · ') : String(v);
    case 'relation': return ctx.relationNames?.[String(v)] ?? String(v);
    case 'relations': return Array.isArray(v) ? v.map((id) => ctx.relationNames?.[String(id)] ?? String(id)).join(' · ') : String(v);
    case 'files': return Array.isArray(v) ? t('oml.value.attachments', '{{n}} adjunto(s)', { n: v.length }) : String(v);
    case 'file': return t('oml.value.attachment', 'adjunto');
    default: return String(v);
  }
}

export interface OmlErrorData { issues?: OmlIssue[]; conflicts?: { field: string; by: string; at: string; current: unknown }[] }

/** Lee `data.oml` del error de tRPC (errorFormatter del backend). */
export function omlErrorData(err: unknown): OmlErrorData | null {
  if (err instanceof TRPCClientError) {
    const data = err.data as { oml?: OmlErrorData } | undefined;
    return data?.oml ?? null;
  }
  return null;
}
export function omlErrorCode(err: unknown): string | null {
  return err instanceof TRPCClientError ? (err.data as { code?: string } | undefined)?.code ?? null : null;
}

/** Nombre visible de una entidad hija (para relaciones y tablas). */
export function entityName(type: OmlEntityType, e: Record<string, unknown>): string {
  const key = OML_SCHEMA[type].nameField;
  const v = e[key];
  return v === null || v === undefined ? '' : String(v);
}

/** Campos que resumen cada entidad en su tabla (además del nombre). */
export const SUMMARY_FIELDS: Record<OmlChildType, string[]> = {
  brand: ['market', 'platform', 'workspaceId', 'active'],
  stakeholder: ['stakeholderType', 'organization', 'role', 'seniority', 'decisionLevel', 'approves', 'active'],
  commChannel: ['channelType', 'intensity'],
  platform: ['usage', 'isCentral', 'accessType', 'accessOwner', 'expiresAt', 'providedByClient'],
  journey: ['status', 'channels', 'measurementStatus', 'uplift', 'incrementalConversions', 'activatedAt'],
  campaign: ['campaignType', 'channels', 'frequency', 'uplift', 'incrementalConversions', 'measuredPeriod'],
  experiment: ['status', 'primaryMetric', 'channels', 'controlGroupLevel', 'decision', 'plannedEndDate'],
  project: ['status', 'briefComplete', 'assetsReceived', 'committedDeadline', 'responsible', 'qaDone'],
  process: ['responsible', 'lastReviewedAt', 'order'],
  channelRule: ['dailyLimitPerUser', 'frequentTimes', 'timesAreHardRule'],
  segment: ['segmentType', 'approxVolume', 'active'],
  tag: ['purpose', 'active'],
  assignment: ['role', 'team', 'dedication', 'from', 'to', 'isPrimary'],
  link: ['linkType', 'resourceOwner', 'requiresSpecialAccess', 'purpose'],
  report: ['frequency', 'format', 'responsible'],
};
